'use client';

import * as React from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';

import { cn } from '../lib/utils';
import type { AutocompleteOption } from './autocomplete-types';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { FormMessageText } from './form';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/** Same shape as `AutocompleteOption`, so a migration only swaps the tag. */
export type OptionPickerOption = AutocompleteOption;

export interface OptionPickerProps {
  /** Committed value. `null`, `undefined` and '' all mean "nothing chosen". */
  value?: string | null;
  /**
   * Called only when the user chooses an option (or the clear row, with '').
   * Never with search text.
   */
  onChange: (value: string) => void;
  options: OptionPickerOption[];
  /** Trigger text when nothing is chosen. */
  placeholder?: string;
  /** Placeholder (and accessible name) of the search box inside the popover. */
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  /** Error message; also marks the trigger `aria-invalid`. */
  error?: string;
  /** Context line rendered above the search box inside the popover. */
  header?: React.ReactNode;
  /**
   * Offer a row that empties the value, while one is chosen and the search box
   * is empty (so Enter after typing always picks a match, never the clear row).
   */
  clearable?: boolean;
  /** Label of that row. */
  clearLabel?: string;
  className?: string;
  /** Trigger id, for an external `<label htmlFor>`, which then names it. */
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /**
   * From `FormControl` (its description and message ids). Merged with this
   * picker's own error id, so both are announced.
   */
  'aria-describedby'?: string;
  /** From `FormControl`. The trigger is invalid when this or `error` says so. */
  'aria-invalid'?: boolean | 'true' | 'false';
  /**
   * Remote search. Called with the popover's search text, after
   * `searchDebounceMs`, and with '' when the popover closes. The caller
   * answers by replacing `options`. The text is still never a value.
   */
  onSearchChange?: (term: string) => void;
  /** Debounce of `onSearchChange`. Default 300. */
  searchDebounceMs?: number;
  /**
   * `options` are already the server's answer to the search text, so skip the
   * built-in label filter (a stage-name hit need not contain the typed text).
   */
  serverFiltered?: boolean;
  /** A search is running. Replaces the empty message while no option shows. */
  loading?: boolean;
  /** Text shown while `loading`. */
  loadingMessage?: string;
  /** Row content, in place of the default icon + label + description. */
  renderOption?: (
    option: OptionPickerOption,
    isSelected: boolean,
  ) => React.ReactNode;
  /** Trigger content for the chosen option, in place of icon + label. */
  renderSelected?: (option: OptionPickerOption) => React.ReactNode;
  /**
   * Free-text entry. While the search box holds text that no option label
   * equals, an extra row offers to create an option from it. Choosing the row
   * awaits this function, then chooses the option it returns: `onChange`
   * fires with that option's value, and the caller must include the option in
   * `options` so the trigger can show it.
   */
  onCreate?: (text: string) => OptionPickerOption | Promise<OptionPickerOption>;
  /** Text of the create row for the typed text. Default: Create "text". */
  createLabel?: (text: string) => string;
  /** Heading of the create row. */
  createGroup?: string;
}

/**
 * Single-select picker (BAL-1132). The trigger is a BUTTON that renders only
 * the committed option or the placeholder; the search box lives inside the
 * popover, is not a form field, and its text is discarded on close. Search
 * text therefore cannot pose as a value, whether typed or autofilled.
 *
 * Use it for every "pick one of these options" field. `Autocomplete` remains
 * for multi-select (chips show the value) and for searches whose typed text
 * is the input itself (address lookup, navigation search).
 *
 * Keyboard: Enter / Space / ArrowDown open; type to filter by label,
 * description or value; arrows move; Enter chooses; Escape closes without
 * changing the value. Focus returns to the trigger on close.
 *
 * Tests: every consumer's test must call
 * `expectPickerShowsOnlyCommittedValue` from
 * `apps/web/app/__tests__/helpers/picker-contract.ts`.
 */
export function OptionPicker({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  disabled = false,
  error,
  header,
  clearable = false,
  clearLabel = 'Clear selection',
  className,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  onSearchChange,
  searchDebounceMs = 300,
  serverFiltered = false,
  loading = false,
  loadingMessage = 'Loading...',
  renderOption,
  renderSelected,
  onCreate,
  createLabel,
  createGroup,
}: OptionPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // The latest callback, so a new function identity does not restart the timer.
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  });
  const baseId = useId();
  // cmdk identifies rows by value; this one cannot collide with an option.
  const clearRowValue = `${baseId}-clear`;
  const errorId = `${baseId}-error`;
  const invalid = !!error || ariaInvalid === true || ariaInvalid === 'true';

  // Search text lives only while the popover is open; closing discards it.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  // Remote search: report the text once typing pauses. Closing empties the
  // text, which reports '' so the caller drops stale results.
  const reportedSearch = useRef('');
  useEffect(() => {
    if (search === reportedSearch.current) return;
    const timer = setTimeout(
      () => {
        reportedSearch.current = search;
        onSearchChangeRef.current?.(search);
      },
      search ? searchDebounceMs : 0,
    );
    return () => clearTimeout(timer);
  }, [search, searchDebounceMs]);

  const selected = value
    ? (options.find((o) => o.value === value) ?? null)
    : null;

  // Grouped rendering when any option carries a `group` (first-seen order);
  // otherwise one unlabeled group.
  const groups = useMemo(() => {
    const buckets = new Map<string, OptionPickerOption[]>();
    for (const option of options) {
      const key = option.group ?? '';
      const bucket = buckets.get(key);
      if (bucket) bucket.push(option);
      else buckets.set(key, [option]);
    }
    return [...buckets.entries()];
  }, [options]);

  const typed = search.trim();
  const showCreateRow =
    !!onCreate &&
    typed !== '' &&
    !options.some((o) => o.label.toLowerCase() === typed.toLowerCase());

  // Emits even when the option is already the shown value: a caller may show
  // a normalised or defaulted value the form does not hold yet, and choosing
  // it must write it.
  const choose = (next: OptionPickerOption) => {
    if (next.disabled) return;
    handleOpenChange(false);
    onChange(next.value);
  };

  let display: React.ReactNode;
  if (selected) {
    // Same visuals as the list row (icon + label), so the trigger reads
    // identically to the option the user picked.
    display = renderSelected ? (
      <span data-slot="picker-label" className="min-w-0 truncate">
        {renderSelected(selected)}
      </span>
    ) : (
      <>
        {selected.icon}
        <span data-slot="picker-label" className="truncate">
          {selected.label}
        </span>
      </>
    );
  } else if (value) {
    // A value with no matching option still shows, so the trigger never
    // claims "nothing chosen" while the form holds something.
    display = (
      <span data-slot="picker-label" className="truncate">
        {value}
      </span>
    );
  } else {
    display = (
      <span
        data-slot="picker-placeholder"
        className="text-muted-foreground truncate"
      >
        {placeholder}
      </span>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={handleOpenChange} modal>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            // Radix sets aria-controls to the popover content's id, which
            // exists while open. The popup is a dialog holding the listbox.
            aria-expanded={open}
            aria-haspopup="dialog"
            // An id means an external `<label htmlFor>` (or FormLabel) names
            // the trigger; a generic aria-label would override it. Same rule
            // as Autocomplete's input (autocomplete-desktop.tsx).
            aria-label={!id && !ariaLabelledBy ? ariaLabel : undefined}
            aria-labelledby={ariaLabelledBy}
            aria-invalid={invalid}
            aria-describedby={
              [ariaDescribedBy, error ? errorId : undefined]
                .filter(Boolean)
                .join(' ') || undefined
            }
            disabled={disabled}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setOpen(true);
              }
            }}
            className={cn(
              'h-11 w-full justify-between px-3 font-normal',
              invalid && 'border-destructive',
            )}
          >
            <span
              data-slot="picker-value"
              className="flex min-w-0 items-center gap-2"
            >
              {display}
            </span>
            <ChevronDownIcon
              className={cn(
                'text-muted-foreground h-4 w-4 shrink-0 transition-transform',
                open && 'rotate-180',
              )}
            />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] min-w-56 p-0"
        >
          {/* Default highlight on the committed option, so Enter keeps it. */}
          <Command
            defaultValue={selected?.value}
            shouldFilter={!serverFiltered}
          >
            {header && (
              <div className="text-muted-foreground border-b px-3 py-2 text-sm">
                {header}
              </div>
            )}
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <CommandList>
              <CommandEmpty>
                {loading ? loadingMessage : emptyMessage}
              </CommandEmpty>
              {groups.map(([group, groupOptions]) => (
                <CommandGroup key={group} heading={group || undefined}>
                  {groupOptions.map((option) => {
                    const isChosen = option.value === selected?.value;
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        keywords={[option.label, option.description ?? '']}
                        disabled={option.disabled}
                        data-checked={isChosen}
                        onSelect={() => choose(option)}
                        className="gap-2"
                      >
                        {renderOption ? (
                          renderOption(option, isChosen)
                        ) : (
                          <>
                            {option.icon}
                            <span className="flex min-w-0 flex-1 flex-col">
                              <span className="truncate">{option.label}</span>
                              {option.description && (
                                <span className="text-muted-foreground truncate text-xs">
                                  {option.description}
                                </span>
                              )}
                            </span>
                          </>
                        )}
                        <CheckIcon
                          className={cn(
                            'h-4 w-4 shrink-0',
                            isChosen ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
              {showCreateRow && (
                <CommandGroup heading={createGroup}>
                  <CommandItem
                    forceMount
                    value={`${baseId}-create`}
                    onSelect={() => {
                      const text = typed;
                      void (async () => {
                        const made = await onCreate?.(text);
                        if (made) choose(made);
                      })();
                    }}
                    className="gap-2"
                  >
                    <span className="truncate">
                      {createLabel ? createLabel(typed) : `Create "${typed}"`}
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              {/* After the options: opening highlights the committed option
                  (Command defaultValue), so Enter right after opening keeps it. */}
              {clearable && value && !search && (
                <CommandGroup>
                  <CommandItem
                    value={clearRowValue}
                    onSelect={() => {
                      handleOpenChange(false);
                      onChange('');
                    }}
                    className="text-muted-foreground"
                  >
                    {clearLabel}
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {error && (
        <p id={errorId} className="text-destructive text-sm">
          <FormMessageText message={error} />
        </p>
      )}
    </div>
  );
}

OptionPicker.displayName = 'OptionPicker';
